import React from 'react';
import {
  Flex,
  useColorMode,
  useIsMobileView,
  Text,
  typography,
  color,
  BookIconSvg,
  YouTubeSvg,
  SpotifySvg,
  GlobeSvg,
  Heading,
} from 'deepstash-ui';
import { CollectionApiResponse } from 'api/api.types';
import NextImage from 'src/components/image/NextImage';
import { SourceType } from 'types/enums';
import { getSingularOrPlural } from 'utils/StringUtils';
import { ClockIconSvg } from 'theme/icons';
import { formatSecondsToNearestMinute } from 'utils/date-utils';

const IMAGE_SIZE = { WIDTH: 220, HEIGHT: 320, BORDER_RADIUS: 16 };

interface CollectionPageHeaderProps {
  collection: CollectionApiResponse;
}

const CollectionPageHeader: React.FC<CollectionPageHeaderProps> = ({
  collection,
}) => {
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();
  const {
    total_article_sources,
    total_book_sources,
    total_podcast_sources,
    total_video_sources,
    time_to_read,
  } = collection;

  const minutesToRead = React.useMemo(() => {
    if (time_to_read !== undefined) {
      return formatSecondsToNearestMinute(time_to_read);
    }
    return undefined;
  }, [time_to_read]);

  const imageComponent = (
    <NextImage
      wrapperStyle={{
        width: IMAGE_SIZE.WIDTH,
        height: IMAGE_SIZE.HEIGHT,
        borderRadius: IMAGE_SIZE.BORDER_RADIUS,
        cursor: 'default',
      }}
      imageUrl={collection.image_compressed || collection.image}
      alt={`${collection.name} Collection`}
      blurhash={collection.image_blurhash}
      priority={true}
    />
  );

  const renderChipSourceType = ({
    sourceType,
    total,
  }: {
    sourceType: SourceType;
    total: number;
  }) => {
    return (
      <Flex
        flexDirection={'row'}
        alignItems="center"
        gridColumnGap={2}
        // bgColor={color[colorMode].surface}
        px={8}
        py={4}
        borderRadius={16}
      >
        {sourceType === SourceType.BOOK ? (
          <BookIconSvg color={color[colorMode].text} />
        ) : null}
        {sourceType === SourceType.BOOK ? (
          <Text
            {...typography.primaryTextFonts.large.regular}
            color={color[colorMode].text}
          >{`${total} ${getSingularOrPlural(total, 'book', 'books')}`}</Text>
        ) : null}
        {sourceType === SourceType.PODCAST ? (
          <SpotifySvg color="#1DB954" />
        ) : null}
        {sourceType === SourceType.PODCAST ? (
          <Text
            {...typography.primaryTextFonts.large.regular}
            color={color[colorMode].text}
          >{`${total} ${getSingularOrPlural(
            total,
            'podcast',
            'podcasts',
          )}`}</Text>
        ) : null}
        {sourceType === SourceType.VIDEO ? <YouTubeSvg color="red" /> : null}
        {sourceType === SourceType.VIDEO ? (
          <Text
            {...typography.primaryTextFonts.large.regular}
            color={color[colorMode].text}
          >{`${total} ${getSingularOrPlural(total, 'video', 'videos')}`}</Text>
        ) : null}
        {sourceType === SourceType.ARTICLE ? (
          <GlobeSvg color={color[colorMode].text} />
        ) : null}
        {sourceType === SourceType.ARTICLE ? (
          <Text
            {...typography.primaryTextFonts.large.regular}
            color={color[colorMode].text}
          >{`${total} ${getSingularOrPlural(
            total,
            'article',
            'articles',
          )}`}</Text>
        ) : null}
      </Flex>
    );
  };

  return (
    <Flex
      flexDirection={isMobileView ? 'column' : 'row'}
      gridColumnGap={32}
      gridRowGap={32}
      alignItems={isMobileView ? 'center' : 'start'}
    >
      {imageComponent}
      <Flex flexDirection="column" flex={1}>
        {!isMobileView && (
          <Text
            {...typography.primaryTextFonts.large.bold}
            color={color[colorMode].textSecondary}
            textTransform="uppercase"
            letterSpacing={2}
            textAlign={{ sm: 'center', md: 'unset' }}
          >
            {'A DEEP DIVE INTO '}
          </Text>
        )}
        <Heading
          {...typography.primaryHeadingFonts.xl.bold}
          {...(isMobileView
            ? typography.primaryHeadingFonts.large.bold
            : typography.primaryHeadingFonts.xl.bold)}
          color={color[colorMode].text}
          textAlign={isMobileView ? 'center' : 'start'}
          size="h1"
        >
          {collection.name}
        </Heading>
        <Text
          {...typography.primaryTextFonts.large.regular}
          color={color[colorMode].text}
          textAlign={isMobileView ? 'center' : 'start'}
          mt={isMobileView ? 16 : 0}
        >
          {collection.description}
        </Text>
        <Flex flexDirection="row" alignItems="center" mt={32} flexWrap="wrap">
          <Text
            {...typography.primaryTextFonts.large.regular}
            color={color[colorMode].text}
          >{`${collection.total_blocks} key ideas from`}</Text>
          {total_book_sources !== undefined && total_book_sources > 0
            ? renderChipSourceType({
                sourceType: SourceType.BOOK,
                total: total_book_sources,
              })
            : null}
          {total_podcast_sources !== undefined && total_podcast_sources > 0
            ? renderChipSourceType({
                sourceType: SourceType.PODCAST,
                total: total_podcast_sources,
              })
            : null}
          {total_video_sources !== undefined && total_video_sources > 0
            ? renderChipSourceType({
                sourceType: SourceType.VIDEO,
                total: total_video_sources,
              })
            : null}
          {total_article_sources !== undefined && total_article_sources > 0
            ? renderChipSourceType({
                sourceType: SourceType.ARTICLE,
                total: total_article_sources,
              })
            : null}
        </Flex>
        {minutesToRead !== undefined ? (
          <Flex flexDirection="row" alignItems="center" mt={8} flexWrap="wrap">
            <Text
              {...typography.primaryTextFonts.large.regular}
              color={color[colorMode].text}
            >{`It takes just`}</Text>

            <Flex
              boxSize={'24px'}
              alignItems="center"
              justifyContent="center"
              ml={8}
              mr={4}
            >
              <ClockIconSvg
                width={24}
                height={24}
                color={color[colorMode].text}
              />
            </Flex>
            <Text
              {...typography.primaryTextFonts.large.regular}
              color={color[colorMode].text}
            >
              {`${minutesToRead} to read`}
            </Text>
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
};

export default CollectionPageHeader;
